import React from 'react';
import { HelpCircle, X } from 'lucide-react';

interface CreditDeductionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentCredits: number;
  deductCredits: number;
  isProcessing: boolean;
  isLoading?: boolean;
  description?: string;
}

export default function CreditDeductionConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  currentCredits = 0,
  deductCredits = 0,
  isProcessing,
  isLoading = false,
  description,
}: CreditDeductionConfirmModalProps) {
  if (!isOpen) return null;

  const safeCurrent = currentCredits ?? 0;
  const safeDeduct = deductCredits ?? 0;
  const hasInsufficientCredits = !isLoading && safeCurrent < safeDeduct;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div
        className="bg-white rounded-3xl border border-slate-100 shadow-2xl w-full max-w-sm p-8 relative animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        {!isProcessing && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Info Icon */}
        <div className="w-14 h-14 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <HelpCircle className="w-7 h-7" />
        </div>

        {/* Title & Description */}
        <div className="text-center space-y-2 mb-6">
          <h3 className="text-xl font-bold text-slate-900 leading-snug">Confirm Credit Deduction</h3>
          <p className="text-xs text-slate-500 leading-relaxed px-1">
            {description ?? `Website Builder costs ${safeDeduct.toLocaleString()} credits to build a new website (min. 6 pages), charged upfront.`}
          </p>
        </div>

        {/* Balance Card */}
        <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4 mb-6 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Current balance</span>
            <span className="text-sm font-bold text-slate-900">{isLoading ? 'Loading...' : `${safeCurrent.toLocaleString()} credits`}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Deduction amount</span>
            <span className="text-sm font-bold text-blue-600">-{safeDeduct.toLocaleString()} credits</span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-2">
            <span className="text-xs font-semibold text-slate-600">Remaining balance</span>
            <span className={`text-sm font-bold ${hasInsufficientCredits ? 'text-red-500' : 'text-slate-950'}`}>
              {isLoading ? '...' : `${(safeCurrent - safeDeduct).toLocaleString()} credits`}
            </span>
          </div>
        </div>

        {/* Insufficient Credits Banner */}
        {hasInsufficientCredits && (
          <div className="mb-6 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-600 text-xs font-semibold text-center leading-normal">
            Insufficient credits. Please top up your balance to proceed.
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 py-3 bg-[#f1f5f9] hover:bg-[#e2e8f0] disabled:opacity-50 rounded-2xl text-xs font-bold text-slate-600 text-center transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isProcessing || isLoading || hasInsufficientCredits}
            className={`flex-1 py-3 disabled:opacity-50 rounded-2xl text-xs font-bold text-center transition-colors cursor-pointer shadow-sm flex items-center justify-center gap-2 ${
              hasInsufficientCredits
                ? 'bg-red-50 text-red-500 border border-red-100 hover:bg-red-50 shadow-none cursor-not-allowed'
                : 'bg-[#0859b8] hover:bg-[#06428b] text-white shadow-blue-100'
            }`}
          >
            {isProcessing || isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                {isProcessing ? 'Processing...' : 'Loading...'}
              </>
            ) : hasInsufficientCredits ? (
              'Insufficient Credits'
            ) : (
              'Confirm & Charge'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
